<template>
  <div class="overflow-hidden">
    <div class="flex sm:h-screen">
      <SideBarMenu />

      <!--overflow-hidden-->
      <div class="flex w-0 flex-1 flex-col">
        <div class="relative flex shrink-0">
          <button
            class="border-r border-secondary-200 px-4 text-secondary-500 focus:bg-secondary-100 focus:text-secondary-600 focus:outline-none md:hidden"
            aria-label="Open sidebar"
            data-testid="mobileSidebarButton"
            @click="openSidebar"
          >
            <Bars3BottomLeftIcon class="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <RouterView />
      </div>
      <BaseAlert
        v-if="displayAlertMessage"
        :type="alertDetailsList.type"
        :message="alertDetailsList.message"
        @close="displayAlertMessage = false"
      />
      <ConfirmDelete
        :open="deleteModalOpen"
        :state="confirmDeleteDetails"
        @close="deleteModalOpen = false"
      />

      <Notifications />

      <BaseSpinner
        v-if="isPageLoader"
        isPageLoader
        :state="isPageLoader"
        transparent
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  inject,
  onMounted,
  ref,
  onBeforeUnmount,
  onUnmounted,
  onBeforeMount,
} from "vue";
import alertMixins, {
  AlertDetails,
  ALERT_DELAY_DEFAULT,
} from "@/mixins/alert-mixins";
import { contactFolderService } from "@/services/contact-folder.service";
import SideBarMenu from "@/components/menu/side-bar-menu.vue";
import { find } from "underscore";
import Notifications from "@/components/notifications.vue";
import { ConfirmDeleteState } from "@/mixins/modal-mixins";
import useBroadcast from "@/helpers/broadcast";
import eventNotifications from "@/composables/event-notifications";
import { useExtraStore } from "./pinia/useExtraStore";
import { useUserAccountsStore } from "./pinia/useUserAccountsStore";
import { useMessageComposeStore } from "./pinia/useMessageComposeStore";
import { storeToRefs } from "pinia";
import { useMenuStore } from "./pinia/useMenuStore";
import { useAuthStore } from "./pinia/useAuthStore";
import { useContactsStore } from "./pinia/useContactsStore";
import { useContactGroupStore } from "./pinia/useContactGroupStore";
import useTheme from "./composables/theme/useTheme";

const { tabReloadEventAttach } = useBroadcast();
const { fetchTags } = useExtraStore();
const { getContactSettings, setSettingsUserProfile } = useUserAccountsStore();
const { setMsisdnOptions } = useMessageComposeStore();
const { setMobileMenuDisplay } = useMenuStore();
const { getDefaultContactFolderId } = storeToRefs(useUserAccountsStore());
const unmount: any = tabReloadEventAttach();
const emitter: any = inject("emitter");
const displayAlertMessage = ref(false);
const confirmDeleteDetails = ref({} as ConfirmDeleteState);
const alertDetailsList = ref({} as AlertDetails);
const deleteModalOpen = ref(false);
const isPageLoader = ref(false);
const { errorHandlerAlert } = alertMixins();
const { openSubscriptions } = eventNotifications();
const {
  setMetaData,
  setDefaultFolder,
  setDefaultFolderDetails,
  fetchFavourites,
  setContactsList,
} = useContactsStore();
const { setTheme } = useTheme();
const updateTimeStamp = setInterval(() => {
  emitter.emit("updateTimeLabel", new Date());
}, 60000);

const { getUserProfile } = storeToRefs(useAuthStore());
const { setContactGroups, fetchContactFilter } = useContactGroupStore();

const openSidebar = async () => {
  setMobileMenuDisplay(true);
};

emitter.on("alert", (alertDetails: AlertDetails) => {
  displayAlertMessage.value = true;
  alertDetailsList.value = { ...alertDetails };
  window.setTimeout(() => {
    displayAlertMessage.value = false;
  }, alertDetails.delay ?? ALERT_DELAY_DEFAULT);
});

emitter.on("confirm-modal", (modalState: ConfirmDeleteState) => {
  deleteModalOpen.value = true;
  confirmDeleteDetails.value = { modalType: "error", ...modalState };
});

emitter.on("loader", (value: boolean) => {
  isPageLoader.value = value;
});

const loadTheme = async () => {
  const tokenData = getUserProfile.value?.decodedData;
  if (tokenData?.brand) {
    setTheme(tokenData.brand?.toLowerCase());
  } else {
    setTheme();
  }
};

onBeforeMount(async () => {
  await loadTheme();
  await setSettingsUserProfile();
  await getContactSettings();
  contactFolderService
    .fetchSettingsContactFolders()
    .then(async ({ data }: any) => {
      if (data && data.length > 0) {
        const folderId = getDefaultContactFolderId.value;
        if (folderId) {
          setMetaData(folderId);
          setDefaultFolder(folderId);
          const contactFolderFound = find(
            data,
            (contactFolder: any) => contactFolder.contactFolderId === folderId,
          );
          if (contactFolderFound) {
            setDefaultFolderDetails(contactFolderFound);
          }
          fetchFavourites(folderId);
          fetchContactFilter({
            folderId: folderId,
          });
          await setContactGroups({
            contactFolderId: folderId,
            pageSize: 25,
            pageIndex: 0,
          });
          setContactsList(folderId);
        } else if (data.length >= 1) {
          setDefaultFolderDetails(data[0]);
          setDefaultFolder(data[0].contactFolderId);
          setMetaData(data[0].contactFolderId);
        }
      }
    })
    .catch(errorHandlerAlert);
});

onMounted(async () => {
  const isFreshLogin = localStorage.getItem("fresh-login");
  if (isFreshLogin) {
    localStorage.removeItem("fresh-login");
  }
  await fetchTags();
  await setMsisdnOptions();
  openSubscriptions();
});

onBeforeUnmount(() => {
  clearInterval(updateTimeStamp);
});

onUnmounted(() => {
  unmount?.();
});
</script>
